import { createContext, useContext, useState, useCallback } from 'react';
import { login as loginApi, register as registerApi } from '../api';

const AuthContext = createContext(null);

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

function save(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => load('hm_auth_user', null));
  const [selectedItems, setSelectedItems] = useState(() =>
    load('hm_selected_items', [])
  );
  // 已认购的付费尊享服务（会员中心「我的付费服务」）
  const [paidServices, setPaidServices] = useState(() =>
    load('hm_paid_services', [])
  );

  const login = useCallback(async (username, password) => {
    const res = await loginApi(username, password);
    const u = {
      name: res.name || username,
      phone: res.phone || username,
      token: res.token,
    };
    setUser(u);
    save('hm_auth_user', u);
    return u;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    save('hm_auth_user', null);
  }, []);

  // 用户注册（演示环境：模拟注册并直接登录）
  const register = useCallback(async (username, password) => {
    const res = await registerApi(username, password);
    const u = {
      name: res.name || username,
      phone: res.phone || username,
      token: res.token,
    };
    setUser(u);
    save('hm_auth_user', u);
    return u;
  }, []);

  // 加入选购物品（按 id 去重）
  const addSelectedItem = useCallback((item) => {
    setSelectedItems((prev) => {
      if (prev.some((it) => it.id === item.id)) return prev;
      const next = [...prev, { ...item, addedAt: new Date().toISOString() }];
      save('hm_selected_items', next);
      return next;
    });
  }, []);

  const removeSelectedItem = useCallback((id) => {
    setSelectedItems((prev) => {
      const next = prev.filter((it) => it.id !== id);
      save('hm_selected_items', next);
      return next;
    });
  }, []);

  // 认购 / 退订付费尊享服务（按 id 去重）
  const addPaidService = useCallback((svc) => {
    setPaidServices((prev) => {
      if (prev.some((it) => it.id === svc.id)) return prev;
      const next = [...prev, { ...svc, addedAt: new Date().toISOString() }];
      save('hm_paid_services', next);
      return next;
    });
  }, []);

  const removePaidService = useCallback((id) => {
    setPaidServices((prev) => {
      const next = prev.filter((it) => it.id !== id);
      save('hm_paid_services', next);
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        register,
        selectedItems,
        addSelectedItem,
        removeSelectedItem,
        paidServices,
        addPaidService,
        removePaidService,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
